import React, { Component } from 'react';

import { Button, FontAwesomeIcon, IconButton } from '@schoolinks/ui-components';

import cn from 'classnames';
import { noop } from 'lodash';
import { func, bool, shape } from 'prop-types';
import { withTranslation } from 'react-i18next';
import Markdown from 'react-markdown';
import { Link } from 'react-router-dom';

import { withState } from 'recompose';

import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons/faArrowUpRightFromSquare';

import Dialog from '@mui/material/Dialog';

import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import VideoPlayer from 'components/Common/VideoPlayer';
import AsyncAction from 'containers/AsyncAction';
import withRouter from 'containers/withRouter';
import { RouterPropTypes } from 'propTypes/RouterPropTypes';

import { isAbsoluteLink } from 'utils/urls';

import s from './FeatureOnboardingModal.css';

const components = {
  a: ({ href, children }) => {
    if (isAbsoluteLink(href)) {
      return (
        <a href={href} target="_blank" rel="noreferrer">
          {children}
        </a>
      );
    }
    return (
      <Link to={href} target="_blank">
        {children}
      </Link>
    );
  },
  pre: ({ value }) => <VideoPlayer url={value} playing width="100%" controls />,
};

@withRouter
@withState('triggerAnimation', 'setTriggerAnimation', undefined)
@withTranslation()
class FeatureOnboardingModal extends Component {
  componentDidUpdate(prevProps) {
    const {
      triggerAnimation: pastAnimation,
      router: { location: pastLocation },
    } = prevProps;
    const {
      onClose,
      setTriggerAnimation,
      triggerAnimation,
      router: { location },
      videoComplete,
      setVideoComplete,
    } = this.props;

    if (pastAnimation !== triggerAnimation) {
      setTimeout(() => {
        onClose();
        setTriggerAnimation(false);
      }, 600);
    }
    // If user just finished an onboarding video, then redirects to look at another one, we want to set videoComplete to false
    if (location?.pathname !== pastLocation?.pathname && videoComplete) {
      setVideoComplete(false);
    }
  }

  markOnboardingComplete = async ({ watchedVideo } = {}) => {
    const {
      completeFeatureOnboarding,
      setVideoComplete,
      feature: { pk },
    } = this.props;

    if (watchedVideo) setVideoComplete(true);
    await completeFeatureOnboarding({ pk });
  };

  render() {
    const {
      open,
      feature: { helpArticleUrl, videoUrl, modalTitle, content, videoFile },
      videoComplete,
      rewatching,
      setTriggerAnimation,
      triggerAnimation,
      setJustWatchedVideo,
      t,
    } = this.props;

    const video = videoFile?.video || videoUrl;
    const transcription = videoFile?.transcription;

    const canViewModule = videoComplete || rewatching;

    return (
      <Dialog
        classes={{
          root: s.root,
          paper: cn(s.paper, {
            [s.closingPaperAnimation]: triggerAnimation || !open,
          }),
        }}
        open={open}
        disableBackdropClick
      >
        <DialogTitle
          classes={{
            root: s.titleRoot,
          }}
          disableTypography
          className={s.title}
        >
          {modalTitle}
          {rewatching ? (
            <IconButton
              className={s.close}
              aria-label={t('Common.FeatureOnboardingModal.CloseModal')}
              onClick={() => {
                setTriggerAnimation(true);
                setJustWatchedVideo(false);
              }}
            >
              <FontAwesomeIcon icon={faXmark} />
            </IconButton>
          ) : null}
        </DialogTitle>
        <DialogContent
          classes={{
            root: cn(s.contentRoot, { [s.mdContentRoot]: Boolean(content) }),
          }}
        >
          {helpArticleUrl && (
            <div>
              <div className={s.sectionHeaderText}>
                {t('Common.FeatureOnboardingModal.HelpArticle')}
              </div>
              <div className={s.descriptionText}>
                {t('Common.FeatureOnboardingModal.VisitHelpCenter')}{' '}
                <AsyncAction
                  action={async () => {
                    await this.markOnboardingComplete();
                  }}
                  wait={600}
                >
                  {({ working, fireAction }) => (
                    <a
                      className={s.link}
                      onClick={!working ? fireAction : () => {}}
                      href={helpArticleUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('Common.FeatureOnboardingModal.ViewHelpArticle', {
                        modalTitle,
                      })}
                      <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                    </a>
                  )}
                </AsyncAction>
              </div>
            </div>
          )}
          {content ? (
            <Markdown components={components} className={s.markdown}>
              {content}
            </Markdown>
          ) : (
            video && (
              <div>
                <div className={s.sectionHeaderText}>
                  {t('Common.FeatureOnboardingModal.VideoTutorial')}
                </div>
                <div className={s.descriptionText}>
                  {t('Common.FeatureOnboardingModal.WatchVideoDescription')}
                </div>
                <AsyncAction
                  action={async () => {
                    await this.markOnboardingComplete({ watchedVideo: true });
                    setJustWatchedVideo(true);
                  }}
                  wait={600}
                >
                  {({ working, fireAction }) => (
                    <VideoPlayer
                      url={video}
                      transcription={transcription}
                      width="100%"
                      controls
                      onEnded={rewatching || working ? () => {} : fireAction}
                    />
                  )}
                </AsyncAction>
              </div>
            )
          )}
        </DialogContent>
        <DialogActions className={s.dialogActions}>
          <AsyncAction
            action={async () => {
              if (!canViewModule) {
                await this.markOnboardingComplete();
              }
              setTriggerAnimation(true);
              setJustWatchedVideo(false);
            }}
            wait={600}
          >
            {({ working, fireAction }) => (
              <Button
                className={s.button}
                color="primary"
                disabled={working}
                label={t('Common.FeatureOnboardingModal.GetStarted')}
                onClick={working ? () => null : fireAction}
                variant="filled"
              />
            )}
          </AsyncAction>
        </DialogActions>
      </Dialog>
    );
  }
}

FeatureOnboardingModal.propTypes = {
  onClose: func,
  open: bool,
  feature: shape({}).isRequired,
  videoComplete: bool.isRequired,
  rewatching: bool,
  setTriggerAnimation: func,
  triggerAnimation: bool,
  setJustWatchedVideo: func,
  setVideoComplete: func,
  completeFeatureOnboarding: func.isRequired,
  router: RouterPropTypes,
  t: func.isRequired,
};

FeatureOnboardingModal.defaultProps = {
  onClose: noop,
  open: false,
  rewatching: false,
  triggerAnimation: false,
  setTriggerAnimation: noop,
  setJustWatchedVideo: noop,
  setVideoComplete: noop,
  router: {},
};

export default FeatureOnboardingModal;
